import { useState, useEffect, useMemo } from "react";

import { useRouter } from "next/router";

import { useTranslator } from "@eo-locale/react";

import { Contribution } from "@/storybook-components";
import { buildImgixUrl, dateWithSiteLocale, stripAllHtml } from "@/lib";
import { useAppSelector } from "@/redux/store/hooks";

import { getRandomColor } from "@/helpers/get-random-color";
import { getVotingStatus } from "@/helpers/ideas";

interface ICardObj {
  id?: string;
  mediaUrl?: string;
  imageUrl?: string;
  author?: string;
  createdAt?: string;
  name?: string;
  description?: string;
  commentsCount?: number;
  handleComment?: any;
  votesCount?: number;
  currentUserVoted?: boolean;
  avatar?: string;
  backgroundColourId?: number;
  userType?: string;
}

const ContributionCard = ({
  cardData,
  clickHandler,
  cardLoaded,
  actionList,
  manageVotes,
  handleComment,
  showCommentIcon = true
}) => {
  const { query } = useRouter();
  const { projectId, brainstormerId } = query;
  const { baseUrl } = useAppSelector((state) => state.adapter.adapter);
  const { settings } = useAppSelector((state) => state.settings);
  const { backgroundColours } = useAppSelector(
    (state) => state.toolCustomizations
  );
  const [data, setData] = useState({ ...cardData });

  useEffect(() => {
    setData(cardData);
  }, [cardData]);

  const {
    id,
    mediaUrl,
    author,
    createdAt,
    name,
    description,
    commentsCount,
    votesCount,
    currentUserVoted,
    avatar,
    backgroundColourId,
    userType
  } = data || ({} as ICardObj);

  const { translate, language } = useTranslator();
  const { moderated } = useAppSelector((state) => state?.site?.site);
  const { login: loggedInUser, type: loggedInUserType } = useAppSelector(
    (state) => state.user?.registeredUser?.profile
  );
  const { brainstormer } = useAppSelector((state) => state.brainstormers);
  const {
    state: brainstormerState,
    votingStartAt,
    votingEndAt,
    disableVoting
  } = brainstormer;
  const { project } = useAppSelector((state) => state.project);
  const { state: projectState } = project;

  const isVotingAllowed =
    (votingStartAt &&
      votingEndAt &&
      getVotingStatus(votingStartAt, votingEndAt) === "active") ||
    getVotingStatus(votingStartAt, votingEndAt) === "ended";

  const isLoggedInUser = author === loggedInUser;
  const isMatchingUserType = userType === loggedInUserType;
  const hasActionOptions =
    moderated ||
    (isLoggedInUser &&
      isMatchingUserType &&
      !isVotingAllowed &&
      projectState !== "archived" &&
      brainstormerState !== "archived");

  const colorList = ["#975A16", "#2C7A7B", "#B83280", "#6B46C1"];
  const getCardColor = useMemo(() => getRandomColor(colorList), [id]);

  return (
    <Contribution
      cardVariant="unstyled"
      username={author || ""}
      contributionTime={
        dateWithSiteLocale(createdAt, language.toLowerCase()) || null
      }
      handleOnBodyClick={clickHandler}
      imageSrc={buildImgixUrl(mediaUrl, 252, 448, "min") || null}
      imageAlt={name || ""}
      showImage
      cardHeading={name || ""}
      cardDescription={description ? stripAllHtml(description) : ""}
      showDescription
      userAvatarSrc={buildImgixUrl(avatar, 40, 40, "min") || null}
      showFooter
      showCommentIcon={showCommentIcon}
      handleComment={handleComment}
      commentsCount={commentsCount || 0}
      isThumbsUp={!disableVoting}
      thumbsUpCount={votesCount || 0}
      handleVoteUp={() => manageVotes(id)}
      shareText={translate("common.share") || ""}
      hasBodyHighlight
      isLoaded={cardLoaded}
      showMoreOptions={hasActionOptions}
      actionList={actionList}
      isVoteActive={currentUserVoted}
      tileBackgroundColor={
        backgroundColourId
          ? backgroundColours.find(
              ({ id }) => `${backgroundColourId}` === `${id}`
            )?.name
          : getCardColor
      }
      ariaLabelVoteIcon={`${translate("common.numberOfVotes", {
        votesCount
      })} ${
        currentUserVoted
          ? translate("common.removeVote")
          : translate("common.addVote")
      }`}
      ariaLabelCommentIcon={`${translate("common.numberOfComments", {
        commentsCount
      })} ${translate("common.addComment")}`}
      isIconButtonPressed={currentUserVoted}
      isShareIcon={!settings.disableSocialMediaSharing}
      ariaLabelShareIcon={translate("common.share")}
      socialShareTitle={name || ""}
      socialShareUrl={`${baseUrl}/${projectId}/brainstormers/${brainstormerId}?idea=${id}`}
    />
  );
};

export default ContributionCard;
